import Card from '../../components/Card/Card';
import { IconAlert, IconBoxArrow } from '../../icons/Icons';
import common from '../../util/scss/common.module.scss';
import PageLayout from '../../layout/PageLayout/PageLayout';
import GlobalContext from '../../contexts/GlobalContext';
import ErrorContext, { ErrorProvider, EErrorUiState } from '../../contexts/ErrorContext';
import styles from './ErrorScreen.module.scss';
import { ELinks } from '../../util/constants';
import { Link } from '@mui/material';
import { FC, useContext } from 'react';

type TErrorProps = {
    header: string;
    subhead?: string;
    cta?: {
        text: string;
        link: string;
    };
};

const ErrorCard: FC<TErrorProps> = (props) => {
    const { header, subhead, cta } = props;
    return (
        <section className={styles.thirdParty}>
            <Card>
                <section className={`${common.icon}`} data-testid={'icon-error'}>
                    <IconAlert width={'65'} height={'65'} fillColor={'#696C74'} />
                </section>
                <header>
                    <h1 className={`${common.pageHeader} ${subhead && common.withSubhead}`}>{header}</h1>
                    {subhead && (
                        <h2 className={common.pageSubhead} data-testid="error-subhead">
                            {subhead}
                        </h2>
                    )}
                </header>
            </Card>
            {cta && (
                <footer>
                    <IconBoxArrow fillColor={'#696C74'} />
                    <Link href={cta.link} target="_blank">
                        {cta.text}
                    </Link>
                </footer>
            )}
        </section>
    );
};

const ErrorUi: FC = () => {
    const { language } = useContext(GlobalContext);
    const { errorUiState, connectedProvider } = useContext(ErrorContext);
    let errorUi: React.ReactElement;
    switch (errorUiState) {
        case EErrorUiState.default:
            errorUi = null;
            break;
        case EErrorUiState.generic:
            errorUi = <ErrorCard header={language.error.generic.header} subhead={language.error.generic.subheader} />;
            break;
        case EErrorUiState.thirdPartyGeneric:
            errorUi = (
                <ErrorCard
                    header={language.error.thirdPartyGeneric.header}
                    cta={{ link: ELinks.userPortal, text: language.error.thirdPartyGeneric.loginCTA }}
                />
            );
            break;
        case EErrorUiState.thirdPartyConnected:
            errorUi = (
                <ErrorCard
                    header={language.error.thirdPartyConnected.header}
                    subhead={`${language.error.thirdPartyConnected.subheader} ${connectedProvider}`}
                    cta={{ link: ELinks.userPortal, text: language.error.thirdPartyConnected.loginCTA }}
                />
            );
            break;
        case EErrorUiState.noCorporateUserFound:
            errorUi = (
                <ErrorCard
                    header={language.error.noCoporateUserFound.header}
                    subhead={language.error.noCoporateUserFound.subheader}
                />
            );
            break;
        case EErrorUiState.corporateUserConflict:
            errorUi = (
                <ErrorCard
                    header={language.error.corporateUserConflict.header}
                    subhead={language.error.corporateUserConflict.subheader}
                />
            );
            break;
        case EErrorUiState.corporateUserNoPermission:
            errorUi = (
                <ErrorCard
                    header={language.error.corporateUserNoPermission.header}
                    subhead={language.error.corporateUserNoPermission.subheader}
                />
            );
            break;
        default:
            throw new Error('Error State Set Incorrectly');
    }
    return errorUi;
};

const ErrorScreen: FC = () => {
    return (
        <ErrorProvider>
            <PageLayout>
                <ErrorUi />
            </PageLayout>
        </ErrorProvider>
    );
};

export default ErrorScreen;
